import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Col, Form, Input, Radio, Row } from 'antd';
import { RcFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { UploadFile } from 'antd/lib';
import cs from 'classnames';

import { selectUser } from 'features/auth/authSelectors';
import CommonsService from 'features/commons/CommonsService';
import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';
import { closeSidebar } from 'features/sidebar/sidebarSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import formatAndOrderData from 'utils/formatAndOrderData';
import { notify } from 'utils/toast/createToast';

import { CW_APP_SOURCE } from 'constants/commons';
import { AlertType, Dictionaries } from 'constants/enums';
import { SECTION_ROW_GUTTER } from 'constants/uiSettings';

import { ReactComponent as FileIcon } from 'assets/icons/file.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';

import styles from './FeedbackView.module.scss';
import {
  FEEDBACK_CATEGORIES_ORDER,
  FEEDBACK_FILE_TYPES,
  MAX_FEEDBACK_FILES,
  MAX_FEEDBACK_FILE_SIZE_MB
} from './constants';

const FeedbackView: FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const feedbackCategoriesDict = useAppSelector(
    selectDictionary(Dictionaries.FeedbackCategory)
  );
  const user = useAppSelector(selectUser);

  const [form] = Form.useForm();
  const fieldsValues = Form.useWatch([], form);
  const catchError = useErrorCatch();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isSubmitting, serIsSubmitting] = useState<boolean>(false);

  const feedbackCategories = formatAndOrderData(
    feedbackCategoriesDict,
    FEEDBACK_CATEGORIES_ORDER
  );
  const supportedFileTypesText = FEEDBACK_FILE_TYPES.split(',')
    .join('/')
    .replaceAll('.', '')
    .toUpperCase();

  const handleRemoveFile = (file: UploadFile): void => {
    const index = fileList.indexOf(file);
    const newFileList = fileList.slice();
    newFileList.splice(index, 1);
    setFileList(newFileList);
  };

  const handleUploadFile = (file: RcFile): boolean => {
    const uploadFile: UploadFile = {
      uid: file.name + Date.now(),
      name: file.name,
      status: 'done',
      originFileObj: file
    };
    setFileList([...fileList, uploadFile]);
    return false;
  };

  const isLimitReached = fileList.length >= MAX_FEEDBACK_FILES;

  const handleCancel = () => {
    setFileList([]);
    serIsSubmitting(false);
    dispatch(closeSidebar());
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch (e) {
      return;
    }

    try {
      serIsSubmitting(true);

      const formData = new FormData();

      const requestData = {
        subject: fieldsValues.subject,
        userFullName: user.name,
        email: user.email,
        description: fieldsValues.description,
        category: fieldsValues.feedbackType,
        appSource: CW_APP_SOURCE,
        appVersion: null
      };

      formData.append(
        'request',
        new Blob([JSON.stringify(requestData)], { type: 'application/json' })
      );

      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });

      await CommonsService.postFeedback(formData);

      notify({
        type: AlertType.Success,
        message: t('feedback.success.message'),
        description: t('feedback.success.description')
      });

      form.resetFields();
      setFileList([]);
      dispatch(closeSidebar());
    } catch (error) {
      catchError({
        error,
        default: {
          description: t('feedback.error.description')
        }
      });
    } finally {
      serIsSubmitting(false);
    }
  };

  return (
    <aside className={styles.wrap}>
      <div className={styles.content}>
        <h2 className={styles.header}>{t('feedback.title')}</h2>
        <div className={styles.description}>{t('feedback.description')}</div>

        <Form
          name={t('feedback.title')}
          initialValues={{}}
          onFinish={handleSubmit}
          autoComplete="off"
          className={styles.form}
          form={form}
        >
          <Row gutter={SECTION_ROW_GUTTER} className={styles.section}>
            <Col span={24}>
              <div className={styles.subHeader}>
                {t('feedback.form.type.label')}
              </div>
              <Form.Item
                name="feedbackType"
                rules={[
                  {
                    required: true,
                    message: t('feedback.form.type.validation')
                  }
                ]}
                className={styles.form}
              >
                <Radio.Group className="vertical-list">
                  {feedbackCategories.map((el) => (
                    <Radio value={el.value}>{el.label}</Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="subject"
                rules={[
                  {
                    required: true,
                    message: t('feedback.form.subject.validation')
                  }
                ]}
                label={t('feedback.form.subject.label')}
              >
                <Input
                  type="text"
                  placeholder={t('feedback.form.subject.placeholder')}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="description"
                rules={[
                  {
                    required: true,
                    message: t('feedback.form.description.validation')
                  }
                ]}
                label={t('feedback.form.description.label')}
              >
                <Input.TextArea
                  placeholder={t('feedback.form.description.placeholder')}
                  rows={5}
                  style={{ resize: 'none' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Row gutter={SECTION_ROW_GUTTER} className={styles.section}>
          <Col span={24}>
            <div className={styles.subHeader}>
              {t('feedback.upload.label', { maxFiles: MAX_FEEDBACK_FILES })}
            </div>
            <div className={styles.description}>
              {t('feedback.upload.description', {
                supportedTypes: supportedFileTypesText,
                maxSize: MAX_FEEDBACK_FILE_SIZE_MB
              })}
            </div>

            <div
              className={cs(styles.uploadWrapper, 'upload-button-container', {
                disabled: isLimitReached
              })}
            >
              <Dragger
                style={{ borderRadius: '18px' }}
                className={styles.upload}
                fileList={fileList}
                beforeUpload={handleUploadFile}
                accept={FEEDBACK_FILE_TYPES}
                maxCount={MAX_FEEDBACK_FILES}
                onRemove={handleRemoveFile}
                iconRender={() => <FileIcon />}
                showUploadList={{
                  showRemoveIcon: true,
                  removeIcon: <TrashIcon className="icon-trash" />
                }}
              >
                <UploadIcon />
                <span>{t('feedback.upload.text')}</span>
              </Dragger>
            </div>
          </Col>
        </Row>
      </div>

      <div className={styles.buttons}>
        <Button danger onClick={handleCancel} className={styles.button}>
          {t('feedback.button.cancel')}
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          className={styles.button}
          loading={isSubmitting}
        >
          {t('feedback.button.submit')}
        </Button>
      </div>
    </aside>
  );
};

export default FeedbackView;
